import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { IconButton, TableContainer } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Pagination from "@material-ui/lab/Pagination";
import EditIcon from "@material-ui/icons/Edit";
import BarChartIcon from "@material-ui/icons/BarChart";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import styled from "styled-components";
import { ReactComponent as FilterIcon } from "assets/icons/Filter.svg";
import { ReactComponent as SortIcon } from "assets/icons/SortIcon.svg";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import Button from "components/button/Button";
import MyFilterButton from "components/MyFilterButton";
import { formatDate } from "helper/dateFormat";
import { numberFormat } from "helper/numberFormat";
import SearchComp from "components/Searchcomponent";
import Loader from "components/loader/Loader";
import { SubHeader } from "layout/SubHeader";
import "./fundraising.scss";
import { fetchCampaigns, updateCampaignStatus } from "./fundraisingSlice";
import CampaignForm from "./CampaignForm";
import CampaignDetail from "./CampaignDetail";
import { fontConfig } from "theme/fontConfig";

// ── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = styled.div`
  width: max-content;
  border-radius: 5px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background-color: ${({ status }) =>
    status === "active" ? "rgb(37 85 239 / 15%)" :
    status === "paused" ? "rgb(250 51 114 / 15%)" :
    status === "completed" ? "#28c23833" :
    status === "draft" ? "#f3f6f9" :
    status === "archived" ? "#f3f6f9" : ""};
  color: ${({ status }) =>
    status === "active" ? "#2555ef" :
    status === "paused" ? "#FA3372" :
    status === "completed" ? "#28c238" :
    status === "draft" ? "#8e8e8e" :
    status === "archived" ? "#8e8e8e" : ""};
`;

const ProgressBar = styled.div`
  height: 6px;
  border-radius: 100px;
  background: #e5e7eb;
  overflow: hidden;
  min-width: 120px;
`;
const ProgressFill = styled.div`
  height: 100%;
  border-radius: 100px;
  background: linear-gradient(90deg, #2555ef, #5b8af5);
  width: ${({ pct }) => pct}%;
`;

// ── MUI styled table cells (matches Communication/Campaigns pattern) ──────────
const TableCellHead = withStyles(() => ({
  root: {
    height: 39,
    padding: "0px 20px",
    borderBottom: "none",
    fontFamily: fontConfig.GeneralTableCellHeadFont.family,
    fontWeight: 700,
    fontSize: 12,
    color: "#4d5565",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
}))(TableCell);

const TableCellBody = withStyles(() => ({
  root: {
    height: 65,
    padding: "0px 20px",
    fontFamily: fontConfig.GeneralTableCellBodyFont.family,
    fontSize: 14,
    color: "#4f4f4f",
    borderBottom: "1px solid rgba(224,224,224,0.5)",
  },
}))(TableCell);

const useStyles = makeStyles({
  tableRow: {
    "&:hover": {
      boxShadow: "rgba(0,0,0,0.35) 0px 5px 15px",
      backgroundColor: "#e6ecf05e",
      cursor: "pointer",
    },
  },
});

const HEADER_COLS = [
  { title: "Title",    sortName: "title" },
  { title: "Status",   sortName: "" },
  { title: "Progress", sortName: "progress" },
  { title: "Raised",   sortName: "raised_amount" },
  { title: "Goal",     sortName: "goal_amount" },
  { title: "End Date", sortName: "end_date" },
  { title: "",         sortName: "" },
];

const PAGE_SIZE = 20;
const pct = (c) => c.goal_amount ? Math.min(100, Math.round((c.raised_amount / c.goal_amount) * 100)) : 0;
const fmt = (n) => numberFormat({ number: n, decimal: 0, style: "currency" });

const appDomain = process.env.REACT_APP_SUBDOMAIN || "easyshul.com";
const getOrg = () => {
  try { return JSON.parse(localStorage.getItem("organization") || "{}"); } catch { return {}; }
};
const campaignPageUrl = (slug) => {
  const org = getOrg();
  return `https://${org.orgkey}.${appDomain}/fundraising/${slug}`;
};

const FundraisingList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { campaigns = [], loading } = useSelector((s) => s.fundraising || {});

  const [formOpen, setFormOpen]       = useState(false);
  const [editCampaign, setEditCampaign] = useState(null);
  const [detailId, setDetailId]       = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage]               = useState(0);
  const [sortField, setSortField]     = useState(null);
  const [sortDir, setSortDir]         = useState("asc");

  const handleSort = (field) => {
    if (!field) return;
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(0);
  };

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    dispatch(fetchCampaigns({ token }));
  }, [dispatch, getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  const handleStatusToggle = async (e, campaign) => {
    e.stopPropagation();
    const token = await getAccessTokenSilently();
    dispatch(updateCampaignStatus({
      id: campaign._id,
      status: campaign.status === "active" ? "paused" : "active",
      token,
    }));
  };

  if (detailId)
    return <CampaignDetail campaignId={detailId} onBack={() => setDetailId(null)} />;

  const filtered = campaigns.filter((c) => {
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    const matchSearch = !searchValue || c.title?.toLowerCase().includes(searchValue.toLowerCase());
    return matchStatus && matchSearch;
  });

  const sorted = sortField
    ? [...filtered].sort((a, b) => {
        let aVal, bVal;
        if (sortField === "progress") {
          aVal = pct(a);
          bVal = pct(b);
        } else if (sortField === "end_date") {
          aVal = a.end_date ? new Date(a.end_date).getTime() : 0;
          bVal = b.end_date ? new Date(b.end_date).getTime() : 0;
        } else if (sortField === "title" || sortField === "status") {
          aVal = (a[sortField] || "").toLowerCase();
          bVal = (b[sortField] || "").toLowerCase();
        } else {
          aVal = a[sortField] || 0;
          bVal = b[sortField] || 0;
        }
        if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
        return 0;
      })
    : filtered;

  const paginated  = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pageCount  = Math.ceil(filtered.length / PAGE_SIZE);

  return (
    <div className="fundraising-page pt-5">
      <div className="header mt-5">
        <div className="header-title">
          <SubHeader className="subheader" title="Fundraising Campaigns" color="#2555EF" />
        </div>
        <Button
          variant="primary"
          onClick={() => { setEditCampaign(null); setFormOpen(true); }}
        >
          <PlusIcon />
          <span>New Campaign</span>
        </Button>
      </div>

      <div className="fundraising-content">
        <div className="filter-row">
          <SearchComp
            value={searchValue}
            setSearchText={(e) => { setPage(0); setSortField(null); setSearchValue(e.target.value); }}
            placeholder="Search"
            style={{ width: "20.5rem", backgroundColor: "#fff" }}
          />
          <MyFilterButton
            title="All"
            items={[
              { label: "Active",    value: "active" },
              { label: "Draft",     value: "draft" },
              { label: "Paused",    value: "paused" },
              { label: "Completed", value: "completed" },
              { label: "Archived",  value: "archived" },
              { label: "All",       value: "all" },
            ]}
            icon={<FilterIcon style={{ width: 19, height: 17 }} />}
            onChange={(val) => { setStatusFilter(val); setPage(0); setSortField(null); }}
          />
        </div>

      {/* ── Table (flat, no card wrapper — matches Communications) ── */}
      <div style={{ paddingLeft: 17, paddingRight: 17 }}>
        <TableContainer className="scroll-table">
          <Table>
            <TableHead
              className="charges-table sticky-table-header"
              style={{ backgroundColor: "#f3f6f9" }}
            >
              <TableRow>
                {HEADER_COLS.map((col, i) => (
                  <TableCellHead key={i}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {col.title}
                      {col.sortName && (
                        <IconButton size="small" onClick={() => handleSort(col.sortName)}>
                          <SortIcon style={{
                            color: sortField === col.sortName ? "#2555ef" : undefined,
                            transform: sortField === col.sortName && sortDir === "desc" ? "scaleY(-1)" : undefined,
                            transition: "transform 0.15s",
                          }} />
                        </IconButton>
                      )}
                    </div>
                  </TableCellHead>
                ))}
              </TableRow>
            </TableHead>
            <TableBody style={{ backgroundColor: "#fff" }}>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <div className="user__loader" style={{ height: 320 }}>
                      <Loader />
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <h6 style={{ height: 200, marginTop: 80, color: "#9ca3af", fontFamily: fontConfig.FundraisingListTextFont.family, fontWeight: 400 }}>
                      {searchValue || statusFilter !== "all"
                        ? "No campaigns match your filters"
                        : "No campaigns yet. Create your first one!"}
                    </h6>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((c) => (
                  <TableRow
                    key={c._id}
                    className={classes.tableRow}
                    onClick={() => setDetailId(c._id)}
                  >
                    <TableCellBody>
                      <span style={{ color: "#4f4f4f", fontWeight: 500 }}>{c.title}</span>
                    </TableCellBody>
                    <TableCellBody>
                      <StatusBadge status={c.status}>
                        {c.status ? c.status[0].toUpperCase() + c.status.slice(1) : "—"}
                      </StatusBadge>
                    </TableCellBody>
                    <TableCellBody style={{ minWidth: 160 }}>
                      <ProgressBar>
                        <ProgressFill pct={pct(c)} />
                      </ProgressBar>
                      <span style={{ fontSize: 11, color: "#9ca3af", marginTop: 3, display: "block", fontFamily: fontConfig.FundraisingListTextFont.family }}>
                        {pct(c)}%
                      </span>
                    </TableCellBody>
                    <TableCellBody>{fmt(c.raised_amount)}</TableCellBody>
                    <TableCellBody>{fmt(c.goal_amount)}</TableCellBody>
                    <TableCellBody>
                      {c.end_date ? formatDate({ date: c.end_date, formatType: "default" }) : "—"}
                    </TableCellBody>
                    <TableCellBody align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setEditCampaign(c); setFormOpen(true); }}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" style={{ color: "#4d5565" }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setDetailId(c._id); }}
                        title="Analytics"
                      >
                        <BarChartIcon fontSize="small" style={{ color: "#4d5565" }} />
                      </IconButton>
                      {c.slug && (
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); window.open(campaignPageUrl(c.slug), "_blank"); }}
                          title="View fundraising page"
                        >
                          <OpenInNewIcon fontSize="small" style={{ color: "#4d5565" }} />
                        </IconButton>
                      )}
                    </TableCellBody>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {pageCount > 1 && (

        <div className="pagination-custom mt-9">
          <Pagination
            count={pageCount}
            color="primary"
            variant="outlined"
            page={page + 1}
            onChange={(_, value) => setPage(value - 1)}
          />
        </div>
      )}

      <CampaignForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        campaign={editCampaign}
        onSaved={load}
      />
      </div>
    </div>
  );
};

export default FundraisingList;
